<template>
  <v-card
    class="tw-min-h-[250px] tw-bg-white tw-rounded-[20px] tw-shadow-none tw-cursor-pointer tw-p-4 tw-box-borders tw-h-auto"
    :class="
      props.job?.userWorkstation_subscription
        ? 'premium-job-gradient'
        : 'free-job-gradient'
    "
  >
    <section class="tw-w-full tw-h-full tw-flex-col-between">
      <div
        class="tw-flex tw-flex-col tw-gap-1 tw-pb-3 tw-box-border tw-border-b-dashed-custom"
      >
        <div class="tw-flex-row-between tw-gap-2">
          <ChipState
            class="tw-relative tw-z-10 !tw-justify-start !tw-p-[0] !tw-gap-2"
            :hasCustomLabel="true"
            bgColor="transparent"
            :hasShadow="false"
          >
            <template #prefix>
              <div
                class="tag tw-rounded-full tw-flex tw-items-center tw-justify-center tw-w-[18px] tw-h-[18px]"
              >
                <img
                  v-if="props.job?.userWorkstation_subscription"
                  class="tw-w-[16px] tw-h-[16px] tw-object-contain"
                  src="@/assets/icons/premium-bg-white.svg"
                />
                <img
                  v-else
                  class="tw-w-[16px] tw-h-[16px] tw-object-contain"
                  src="@/assets/icons/free-member-bg-white.svg"
                />
              </div>
            </template>
            <template #custom-label>
              <Text variant="span" whiteSpace="nowrap">
                {{
                  props.job?.userWorkstation_subscription ? "PREMIUM" : "FREE"
                }}
                <Text variant="span" whiteSpace="nowrap" textColor="#FFA500"
                  >PROPERTY MEMBER</Text
                >
              </Text>
            </template>
          </ChipState>
          <Text variant="span"
            >{{ elapsedTime
            }}<Text variant="span" textColor="#8587A4"> ago</Text></Text
          >
        </div>
      </div>

      <div
        class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-start tw-gap-2"
      >
        <Text
          variant="h5"
          textAlign="left"
          whiteSpace="noWrap"
          class="tw-overflow-hidden tw-text-ellipsis"
          >{{ job?.name }}</Text
        >
        <div class="tw-flex-row-between tw-gap-2 tw-flex-wrap">
          <HistoricalProjectChip />
          <div class="tw-flex tw-items-center tw-gap-1">
            <Text variant="span">
              1
              <Text variant="span" textColor="#8587A4">Property</Text></Text
            >
            <el-divider direction="vertical"></el-divider>
            <ResidentialChip />
            <!-- <CommercialChip v-else /> -->
          </div>
        </div>
        <div class="tw-w-full">
          <Text
            ref="descriptionElement"
            variant="span"
            textWeight="400"
            textAlign="left"
            lineHeight="20px"
            textColor="rgba(12, 15, 74, 0.5)"
            class="description-text tw-overflow-hidden tw-text-ellipsis"
            v-html="job?.description"
          >
          </Text>
          <Text
            textColor="#4F55F0"
            variant="span"
            textWeight="400"
            v-if="isOverflowing"
            >Read More</Text
          >
        </div>
      </div>

      <div
        class="tw-w-full tw-flex tw-flex-col tw-gap-1"
        :class="{
          'tw-border-t-dashed-custom tw-pt-3 tw-box-border': hasProgressBar,
        }"
      >
        <div class="tw-w-full tw-flex-row-between">
          <ChipState
            label="Details"
            bgColor="#FAA500"
            textColor="#FFF"
            class="!tw-rounded-full"
          >
            <template #postfix>
              <div
                class="tw-rounded-full tw-w-[16px] tw-h-[16px] tw-flex tw-items-center tw-justify-center"
              >
                <v-icon
                  icon="mdi-chevron-right"
                  color="#FFF"
                  size="x-small"
                ></v-icon>
              </div>
            </template>
          </ChipState>
        </div>
      </div>
    </section>
  </v-card>
</template>
<script lang="ts" setup>
import moment from "moment";
import { useRouter } from "vue-router";
import { ref, onMounted, nextTick, onUnmounted, computed } from "vue";

import Text from "@/core/components/ui/general/Text.vue";
import ChipState from "@/core/components/common/ChipState.vue";
import CommercialChip from "@/core/uni-components/Chips/CommercialChip.vue";
import ResidentialChip from "@/core/uni-components/Chips/ResidentialChip.vue";
import HistoricalProjectChip from "@/core/uni-components/Chips/HistoricalProjectChip.vue";
import { ProjectJobStatusEnum } from "@/core/enums/ProjectsEnum";
import { PROJECT_DETAILS_ROUTE } from "../routes";

const router = useRouter();

const props = defineProps({
  job: {
    type: Object,
    required: true,
  },
  hasProgressBar: {
    type: Boolean,
    default: false,
  },
});

const descriptionElement = ref(null) as any;
const isOverflowing = ref(false);

const projectStatusItems = ref([
  {
    text: "All",
    value: "",
    icon: "trade-network-list",
  },
  {
    text: "Awaiting estimates",
    value: ProjectJobStatusEnum.AWAITING_QUOTES,
    icon: "awaiting-estimates-icon",
  },
  {
    text: "Estimates received",
    value: ProjectJobStatusEnum.QUOTES_RECEIVED,
    icon: "estimates-receive-icon",
  },
  {
    text: "In progress",
    value: ProjectJobStatusEnum.IN_PROGRESS,
    icon: "in-progress-icon",
  },
  {
    text: "Cancelled",
    value: ProjectJobStatusEnum.CANCELLED,
    icon: "cancelled-icon",
  },
]);

const checkOverflow = () => {
  nextTick(() => {
    if (descriptionElement.value) {
      const el = descriptionElement.value;
      isOverflowing.value = el.scrollHeight > el.clientHeight;
      console.log(
        "Scroll Height:",
        el.scrollHeight,
        "Client Height:",
        el.clientHeight,
        "Is Overflowing:",
        isOverflowing.value
      );
    }
  });
};

// Method to calculate elapsed time
const calculateElapsedTime = (date: string): string => {
  const now = moment();
  const createdDate = moment(date);

  const seconds = now.diff(createdDate, "seconds");
  const minutes = now.diff(createdDate, "minutes");
  const hours = now.diff(createdDate, "hours");
  const days = now.diff(createdDate, "days");
  const weeks = now.diff(createdDate, "weeks");
  const months = now.diff(createdDate, "months");
  const years = now.diff(createdDate, "years");

  if (years > 0) {
    return `${years} Year${years > 1 ? "s" : ""}`;
  } else if (months > 0) {
    return `${months} Month${months > 1 ? "s" : ""}`;
  } else if (weeks > 0) {
    return `${weeks} Week${weeks > 1 ? "s" : ""}`;
  } else if (days > 0) {
    return `${days} Day${days > 1 ? "s" : ""}`;
  } else if (hours > 0) {
    return `${hours} Hrs${hours > 1 ? "" : ""}`;
  } else if (minutes > 0) {
    return `${minutes} Min${minutes > 1 ? "s" : ""}`;
  } else {
    return `${seconds} Sec${seconds > 1 ? "s" : ""}`;
  }
};

// Reactive reference to hold the elapsed time
const elapsedTime = ref(calculateElapsedTime(props.job?.dateCreated));

// Update the elapsed time every minute
const realTimeSetInterval = setInterval(() => {
  elapsedTime.value = calculateElapsedTime(props.job?.dateCreated);
}, 1000);

const getProjectStatus = (type: string) => {
  const projectStatus = projectStatusItems.value.find(
    (item) => item.value === type
  ) as any;
  return projectStatus?.text;
};

const onClickProject = (value: string, projectType: string) => {
  router.push({ name: PROJECT_DETAILS_ROUTE, params: { refId: value } });
  // if (projectType == "video consultation") {
  //   const routerData = router.resolve({
  //     name: PROJECT_DETAILS_ROUTE,
  //     params: { refId: value },
  //   });
  //   window.open(routerData.href, "_blank");
  // }
};

onMounted(() => {
  checkOverflow();
  window.addEventListener("resize", checkOverflow);
});
onUnmounted(() => {
  clearInterval(realTimeSetInterval);
});
</script>
<style lang="scss" scoped>
.premium-job-gradient {
  background: linear-gradient(white, white) padding-box,
    linear-gradient(to bottom, #ffa500, white) border-box;
  border: 2px solid transparent;
}

.free-job-gradient {
  background: linear-gradient(white, white) padding-box,
    linear-gradient(to bottom, #686df2, white) border-box;
  border: 2px solid transparent;
}

.tag {
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
}

.description-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
